/**
 * Projects Page
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { fetchProjects, createProject, updateProject, deleteProject } from '@/store/projectSlice';
import { ProjectCard } from '@/components/ProjectCard';
import { Modal } from '@/components/Modal';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';
import { Plus, Search, Edit, X, Check } from 'lucide-react';
import Share from '@/components/Share';
import { Project } from '@/types';
import { CreateProjectModal } from '@/components/ProjectModels/CreateProjectModal';
import toast from 'react-hot-toast';

export const Projects = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { projects, loading } = useAppSelector((state) => state.projects);
  const { user, isAdmin } = useAppSelector((state) => state.auth);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    key: '',
  });
  // Edit/Delete state
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [editName, setEditName] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<Project | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // Share handled by shared component

  useEffect(() => {
    // Only fetch active projects (soft-deleted projects have is_active=false)
    dispatch(fetchProjects({ is_active: true,organization_id: user?.organization_id  }));
  }, [dispatch]);

  // Handle create query parameter from Sidebar
  useEffect(() => {
    console.log("user details ----->", user);
    if (searchParams.get('create') === '1' && isAdmin) {
      setIsModalOpen(true);
      // Remove the query parameter from URL
      searchParams.delete('create');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, isAdmin]);

  // (no local share logic)

  // const handleCreateProject = async (e: React.FormEvent) => {
  //   e.preventDefault();
  //   try {
  //     // Use a default organization ID if user doesn't have one
  //     const projectData = {
  //       ...formData,
  //       organization_id: user?.organization_id || 'default-org-id'
  //     };
  //     await dispatch(createProject(projectData)).unwrap();
  //     setIsModalOpen(false);
  //     setFormData({ name: '', description: '', key: '' });
  //   } catch (error) {
  //     console.error('Failed to create project:', error);
  //   }
  // };

  const filteredProjects = projects.filter((project) =>
    project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    project.key.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Handle edit project
  const handleStartEdit = (project: Project) => {
    setEditingProject(project);
    setEditName(project.name);
  };

  const handleCancelEdit = () => {
    setEditingProject(null);
    setEditName('');
  };

  const handleSaveEdit = async () => {
    if (!editingProject || !editName.trim() || editName.trim().length < 3) {
      return;
    }
    
    setIsUpdating(true);
    try {
      await dispatch(updateProject({ 
        id: editingProject.id, 
        data: { name: editName.trim() } 
      })).unwrap();
      setEditingProject(null);
      setEditName('');
      dispatch(fetchProjects({ is_active: true, organization_id: user?.organization_id  }));
    } catch (error) {
      // Error toast is handled in the thunk
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle delete project
  const handleDeleteClick = (project: Project) => {
    setShowDeleteConfirm(project);
  };

  const handleConfirmDelete = async () => {
    if (!showDeleteConfirm) return;
    
    setIsDeleting(true);
    try {
      await dispatch(deleteProject(showDeleteConfirm.id)).unwrap();
      dispatch(fetchProjects({ is_active: true, organization_id: user?.organization_id  }));
    } catch (error) {
      // Error toast is handled in the thunk
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(null);
    }
  };

  const handleCreateProject = async (projectData:any) => {
    try {
      await dispatch(createProject(projectData)).unwrap();
   
    } catch (error) {
      toast.error("Something went wrong")
    }
    finally{
      setIsModalOpen(false);
    }
    

  };

  // (copy link handled by shared Share)

  return (
    <div className="space-y-6 pt-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Projects</h1>
          <p className="text-gray-600 mt-1">Manage all your projects</p>
        </div>
        <div className="flex items-center gap-2 relative">
          <Share label="Share" className="btn btn-secondary flex items-center gap-2" subject="Shared view: Projects" />
          {isAdmin && (
            <button
              onClick={() => setIsModalOpen(true)}
              className="btn btn-primary flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              New Project
            </button>
          )}
        </div>
      </div>
      {/* Shared Share component handles popover */}

      {/* Search */}
      <div className="relative mb-6 w-full max-w-md">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
      <input
        type="text"
        placeholder="Search projects..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className="w-full h-11 pl-10 pr-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm placeholder-gray-400"
      />
    </div>

      

      {/* Projects Grid */}
      {loading ? (
        <div className="text-center py-12">Loading projects...</div>
      ) : filteredProjects.length === 0 ? (
        <div className="card text-center py-12 text-gray-500">
          {searchQuery ? 'No projects found matching your search.' : 'No projects yet. Create your first project!'}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project) => (
            <div key={project.id} className="relative">
              {editingProject?.id === project.id ? (
                <div className="card">
                  <div className="flex items-center gap-2 mb-3">
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSaveEdit();
                        } else if (e.key === 'Escape') {
                          handleCancelEdit();
                        }
                      }}
                      className="flex-1 text-lg font-semibold px-2 py-1 border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={isUpdating}
                      autoFocus
                      maxLength={200}
                    />
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={handleSaveEdit}
                        disabled={isUpdating || !editName.trim() || editName.trim().length < 3}
                        className="p-1.5 hover:bg-green-100 rounded text-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        title="Save"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        disabled={isUpdating}
                        className="p-1.5 hover:bg-red-100 rounded text-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        title="Cancel"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500">
                    <span className="badge badge-primary">{project.key}</span>
                  </div>
                </div>
              ) : (
                <ProjectCard 
                  project={project} 
                  onEdit={handleStartEdit}
                  onDelete={handleDeleteClick}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create Project Modal */}
      {/* <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Create New Project"
      >
        <form onSubmit={handleCreateProject} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Project Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input"
              placeholder="My Project"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Project Key
            </label>
            <input
              type="text"
              value={formData.key}
              onChange={(e) => setFormData({ ...formData, key: e.target.value.toUpperCase() })}
              className="input"
              placeholder="PROJ"
              required
              pattern="[A-Z]{2,10}"
              title="2-10 uppercase letters"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="input resize-none"
              rows={4}
              placeholder="Project description..."
            />
          </div>

          <div className="flex gap-3">
            <button type="submit" className="btn btn-primary flex-1">
              Create Project
            </button>
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="btn btn-secondary flex-1"
            >
              Cancel
            </button>
          </div>
        </form>
      </Modal> */}
      <CreateProjectModal
  isOpen={isModalOpen}
  onClose={() => setIsModalOpen(false)}
  onSubmit={handleCreateProject}
  organizationId={user?.organization_id || ''}
/>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <DeleteConfirmDialog
          isOpen={!!showDeleteConfirm}
          itemName={showDeleteConfirm.name}
          itemType="project"
          onConfirm={handleConfirmDelete}
          onCancel={() => setShowDeleteConfirm(null)}
          isLoading={isDeleting}
          customMessage="Are you sure you want to delete this project? This will hide the project and all its tasks, stories, epics, and issues from the system. This action cannot be undone."
        />
      )}
    </div>
  );
};

